// Shape guard for the figures an Explain session carries. The server
// (functions/explainVisuals.js) checks the math and computes every number the
// renderer draws; this only stops a corrupted or legacy document from reaching
// ExplainVisual. It never evaluates an expression.

// The kinds ExplainVisual draws. Any other kind is dropped here, so its step
// still renders as text.
const DRAWN_KINDS = new Set(['function', 'limit', 'table', 'geometry', 'histogram', 'boxPlot'])
const PLOT_VARIABLES = new Set(['x', 'y', 't', 'theta'])
const ASYMPTOTE_AXES = new Set(['vertical', 'horizontal'])
const GEOMETRY_ID = /^[A-Z][A-Z0-9]{0,2}$/

// The server's own bounds; nothing it writes is larger.
const MAX_ALT_CHARS = 180
const MAX_EXPRESSION_CHARS = 200
const MAX_PLOT_SAMPLES = 81
const MAX_HIGHLIGHTS = 4
const MAX_HIGHLIGHT_LABEL_CHARS = 40
const MAX_ASYMPTOTES = 6
const MAX_TABLE_COLUMNS = 6
const MAX_TABLE_ROWS = 12
const MAX_TABLE_HEADER_CHARS = 40
const MAX_TABLE_CELL_CHARS = 80
const MAX_GEOMETRY_ELEMENTS = 24
const MAX_GEOMETRY_BOX_SIZE = 2000
const MAX_POINT_LABEL_CHARS = 24
const MAX_GEOMETRY_TEXT_CHARS = 60
const MAX_HISTOGRAM_BINS = 12
const EXPRESSION_CHARACTERS = /^[0-9a-z.+\-*/^(), ]+$/

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const isPresent = (value) => value !== undefined && value !== null

// Markup delimiters and C0/C1 control characters. The server rejects both in
// figure text, so a string carrying one was not written by it.
const hasUnsafeCharacter = (text) => {
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index)
    if (code === 60 || code === 62 || code < 32 || (code >= 127 && code < 160)) {
      return true
    }
  }
  return false
}

const isSafeText = (value, maxChars) =>
  typeof value === 'string' && value.length <= maxChars && !hasUnsafeCharacter(value)

const readAlt = (value, fallback) =>
  isSafeText(value, MAX_ALT_CHARS) && value.trim() ? value : fallback

const readRange = (value) =>
  Array.isArray(value) && value.length === 2 &&
  Number.isFinite(value[0]) && Number.isFinite(value[1]) && value[0] < value[1]
    ? [value[0], value[1]]
    : null

const readPoint = (value) =>
  isPlainObject(value) && Number.isFinite(value.x) && Number.isFinite(value.y)
    ? { x: value.x, y: value.y }
    : null

const readSamples = (value) => {
  if (!Array.isArray(value) || value.length < 2 || value.length > MAX_PLOT_SAMPLES) {
    return null
  }
  const samples = []
  for (const sample of value) {
    if (!isPlainObject(sample) || !Number.isFinite(sample.x) ||
      !(sample.y === null || Number.isFinite(sample.y))) {
      return null
    }
    samples.push({ x: sample.x, y: sample.y })
  }
  return samples
}

// Annotations follow the server's own policy: a bad one is dropped and the
// curve stays.
const readHighlights = (value) => {
  if (!Array.isArray(value)) return []
  return value.slice(0, MAX_HIGHLIGHTS)
    .filter((entry) => readPoint(entry) && isSafeText(entry.label, MAX_HIGHLIGHT_LABEL_CHARS))
    .map((entry) => ({ x: entry.x, y: entry.y, label: entry.label }))
}

const readAsymptotes = (value) => {
  if (!Array.isArray(value)) return []
  return value.slice(0, MAX_ASYMPTOTES)
    .filter((entry) => isPlainObject(entry) && ASYMPTOTE_AXES.has(entry.axis) &&
      Number.isFinite(entry.at))
    .map((entry) => ({ axis: entry.axis, at: entry.at }))
}

// Rows are stored as `{ cells }` because Firestore cannot hold an array
// directly inside an array. A bare array row is accepted the way the server
// accepts it.
const readTable = (value) => {
  if (!isPlainObject(value)) return null
  const { columns, rows } = value
  if (!Array.isArray(columns) || !columns.length || columns.length > MAX_TABLE_COLUMNS ||
    !columns.every((header) => isSafeText(header, MAX_TABLE_HEADER_CHARS)) ||
    !Array.isArray(rows) || !rows.length || rows.length > MAX_TABLE_ROWS) {
    return null
  }
  const tableRows = []
  for (const row of rows) {
    const cells = Array.isArray(row) ? row : (isPlainObject(row) ? row.cells : null)
    if (!Array.isArray(cells) || cells.length !== columns.length ||
      !cells.every((cell) => isSafeText(cell, MAX_TABLE_CELL_CHARS))) {
      return null
    }
    tableRows.push({ cells: [...cells] })
  }
  return { columns: [...columns], rows: tableRows }
}

const acceptPlot = (raw) => {
  const { kind, expr, variable } = raw
  const domain = readRange(raw.domain)
  const yDomain = readRange(raw.yDomain)
  const samples = readSamples(raw.samples)
  if (!domain || !yDomain || !samples || !PLOT_VARIABLES.has(variable) ||
    typeof expr !== 'string' || expr.length > MAX_EXPRESSION_CHARS ||
    !EXPRESSION_CHARACTERS.test(expr)) {
    return null
  }
  if (kind === 'limit' && !Number.isFinite(raw.approach)) return null

  const visual = {
    kind,
    alt: readAlt(raw.alt, `Graph of ${expr}`),
    expr,
    variable,
    domain,
    samples,
    yDomain,
    highlights: readHighlights(raw.highlights),
    asymptotes: readAsymptotes(raw.asymptotes),
    // The value table is an extra; a damaged one leaves the graph standing.
    table: readTable(raw.table)
  }
  if (kind === 'limit') {
    visual.approach = raw.approach
    visual.hole = readPoint(raw.hole)
  }
  return visual
}

const acceptTable = (raw) => {
  const table = readTable(raw)
  if (!table) return null
  return {
    kind: 'table',
    alt: readAlt(raw.alt, `Table with columns ${table.columns.join(', ')}`),
    columns: table.columns,
    rows: table.rows
  }
}

const readPair = (value) =>
  Array.isArray(value) && value.length === 2 && Number.isFinite(value[0]) && Number.isFinite(value[1])
    ? [value[0], value[1]]
    : null

const readBoundingBox = (value) => {
  if (!Array.isArray(value) || value.length !== 4 || !value.every(Number.isFinite)) return null
  const [xMin, yMin, xMax, yMax] = value
  return xMax > xMin && yMax > yMin &&
    xMax - xMin <= MAX_GEOMETRY_BOX_SIZE && yMax - yMin <= MAX_GEOMETRY_BOX_SIZE
    ? [xMin, yMin, xMax, yMax]
    : null
}

// Ids declared once with finite coordinates. A repeated id is ambiguous, so
// neither copy is kept and anything naming it drops with it.
const readGeometryPoints = (elements) => {
  const points = new Map()
  const declared = new Set()
  for (const element of elements) {
    if (!isPlainObject(element) || element.type !== 'point' ||
      typeof element.id !== 'string' || !GEOMETRY_ID.test(element.id)) {
      continue
    }
    if (declared.has(element.id)) {
      points.delete(element.id)
      continue
    }
    declared.add(element.id)
    const coords = readPair(element.coords)
    if (coords) points.set(element.id, coords)
  }
  return points
}

// A reference that names a kept point; two that name different places.
const pointRef = (value, points) => (typeof value === 'string' && points.has(value) ? value : null)
const apart = (points, first, second) => {
  const [x1, y1] = points.get(first)
  const [x2, y2] = points.get(second)
  return x1 !== x2 || y1 !== y2
}

// Rebuilt with only the keys of its type. An element the drawing cannot
// resolve is dropped and the rest of the figure stays.
const readGeometryElement = (element, points) => {
  if (!isPlainObject(element)) return null
  switch (element.type) {
    case 'point': {
      if (!pointRef(element.id, points)) return null
      const point = { type: 'point', id: element.id, coords: points.get(element.id) }
      if (isSafeText(element.label, MAX_POINT_LABEL_CHARS) && element.label.trim()) point.label = element.label
      return point
    }
    case 'segment':
    case 'line': {
      const from = pointRef(element.from, points)
      const to = pointRef(element.to, points)
      return from && to && apart(points, from, to) ? { type: element.type, from, to } : null
    }
    case 'polygon': {
      const { vertices } = element
      if (!Array.isArray(vertices) || vertices.length < 3 || vertices.length > MAX_GEOMETRY_ELEMENTS ||
        !vertices.every((id) => pointRef(id, points)) || new Set(vertices).size !== vertices.length) {
        return null
      }
      return { type: 'polygon', vertices: [...vertices] }
    }
    case 'circle': {
      const center = pointRef(element.center, points)
      if (!center || isPresent(element.radius) === isPresent(element.through)) return null
      if (isPresent(element.through)) {
        const through = pointRef(element.through, points)
        return through && apart(points, center, through) ? { type: 'circle', center, through } : null
      }
      return Number.isFinite(element.radius) && element.radius > 0 && element.radius <= MAX_GEOMETRY_BOX_SIZE
        ? { type: 'circle', center, radius: element.radius }
        : null
    }
    case 'angle': {
      const ids = Array.isArray(element.points) && element.points.length === 3
        ? element.points.map((id) => pointRef(id, points))
        : []
      if (ids.length !== 3 || ids.includes(null) ||
        !apart(points, ids[1], ids[0]) || !apart(points, ids[1], ids[2])) {
        return null
      }
      return { type: 'angle', points: ids }
    }
    case 'text': {
      const at = readPair(element.at)
      return at && isSafeText(element.content, MAX_GEOMETRY_TEXT_CHARS) && element.content.trim()
        ? { type: 'text', at, content: element.content }
        : null
    }
    default:
      return null
  }
}

const acceptGeometry = (raw) => {
  const boundingBox = readBoundingBox(raw.boundingBox)
  if (!boundingBox || !Array.isArray(raw.elements) || raw.elements.length > MAX_GEOMETRY_ELEMENTS) {
    return null
  }
  const points = readGeometryPoints(raw.elements)
  const elements = raw.elements
    .map((element) => readGeometryElement(element, points))
    .filter(Boolean)
  if (!elements.length) return null
  const ids = [...points.keys()]
  return {
    kind: 'geometry',
    alt: readAlt(raw.alt, ids.length ? `Diagram with points ${ids.join(', ')}` : 'Geometry diagram'),
    boundingBox,
    elements
  }
}

// Bins as the server writes them: ascending, each starting where the last
// ended, with whole-number counts. The browser never re-bins the data.
const readBins = (value) => {
  if (!Array.isArray(value) || !value.length || value.length > MAX_HISTOGRAM_BINS) return null
  const bins = []
  for (const bin of value) {
    if (!isPlainObject(bin) || !Number.isFinite(bin.start) || !Number.isFinite(bin.end) ||
      !(bin.end > bin.start) || !Number.isInteger(bin.count) || bin.count < 0 ||
      (bins.length && bin.start !== bins[bins.length - 1].end)) {
      return null
    }
    bins.push({ start: bin.start, end: bin.end, count: bin.count })
  }
  return bins.some((bin) => bin.count > 0) ? bins : null
}

const acceptHistogram = (raw) => {
  const bins = readBins(raw.bins)
  if (!bins || !Number.isFinite(raw.binWidth) || raw.binWidth <= 0) return null
  return {
    kind: 'histogram',
    alt: readAlt(raw.alt, `Histogram with ${bins.length} bins`),
    binWidth: raw.binWidth,
    bins
  }
}

// The summary the server computed. Raw `values` are never read: the client
// draws the five numbers, it does not derive them.
const acceptBoxPlot = (raw) => {
  if (!isPlainObject(raw.fiveNumber)) return null
  const { min, q1, median, q3, max } = raw.fiveNumber
  const ordered = [min, q1, median, q3, max]
  if (!ordered.every(Number.isFinite) ||
    ordered.some((value, index) => index > 0 && value < ordered[index - 1])) {
    return null
  }
  return {
    kind: 'boxPlot',
    alt: readAlt(raw.alt, `Box plot with minimum ${min}, first quartile ${q1}, median ${median}, ` +
      `third quartile ${q3}, and maximum ${max}`),
    fiveNumber: { min, q1, median, q3, max }
  }
}

/**
 * A stored or returned figure, rebuilt with only the keys ExplainVisual reads,
 * or null when it is absent, of a kind the client does not draw, or not the
 * shape the server writes.
 * @param {*} raw - `step.visual` or `finalVisual` as it arrived.
 * @return {object|null} Figure safe to hand to ExplainVisual.
 */
export const acceptExplainVisual = (raw) => {
  if (!isPlainObject(raw) || !DRAWN_KINDS.has(raw.kind)) return null
  switch (raw.kind) {
    case 'table':
      return acceptTable(raw)
    case 'geometry':
      return acceptGeometry(raw)
    case 'histogram':
      return acceptHistogram(raw)
    case 'boxPlot':
      return acceptBoxPlot(raw)
    default:
      return acceptPlot(raw)
  }
}
